import { useState } from 'react';

interface OnboardingWizardProps {
  serviceKey: string;
  onComplete: () => void;
  onCreateChildKey: () => void;
}

const API_URL = 'https://api.keypalace.dev';

const PERMISSION_ROWS = [
  { label: 'Get Balance', service: true, child: true },
  { label: 'Deduct', service: true, child: true },
  { label: 'Add Credits', service: true, child: false },
  { label: 'Set Credits', service: true, child: false },
];

export function OnboardingWizard({ serviceKey, onComplete, onCreateChildKey }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState<'key' | 'curl' | null>(null);

  const maskedKey = serviceKey.slice(0, 8) + '•'.repeat(Math.max(0, serviceKey.length - 12)) + serviceKey.slice(-4);
  const displayKey = showKey ? serviceKey : maskedKey;

  const curlCommand = `curl -X GET "${API_URL}/api/credits/balance?userId=test_user" \\
  -H "x-api-key: ${displayKey}"`;

  const copyToClipboard = (type: 'key' | 'curl') => {
    const rawText = type === 'curl'
      ? `curl -X GET "${API_URL}/api/credits/balance?userId=test_user" \\\n  -H "x-api-key: ${serviceKey}"`
      : serviceKey;
    navigator.clipboard.writeText(rawText);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="terminal-box w-full max-w-2xl p-8">
        <div className="flex justify-center gap-3 mb-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1 w-12 rounded-full transition-all duration-300 ${
                i === step
                  ? 'bg-terminal-orange terminal-glow'
                  : i < step
                    ? 'bg-terminal-orange-dim'
                    : 'bg-terminal-black border border-terminal-orange-dim'
              }`}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-6 animate-in fade-in">
            <h2 className="text-terminal-orange-bright text-xl font-mono tracking-wide">
              {'>'} Your Service Key
            </h2>

            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={displayKey}
                className="flex-1 bg-terminal-black text-terminal-orange font-mono text-sm px-4 py-3 border border-terminal-orange-dim rounded focus:outline-none"
              />
              <button
                onClick={() => setShowKey(!showKey)}
                className="terminal-button px-3 py-3 text-sm font-mono"
              >
                {showKey ? '[ HIDE ]' : '[ SHOW ]'}
              </button>
              <button
                onClick={() => copyToClipboard('key')}
                className="terminal-button px-3 py-3 text-sm font-mono"
              >
                {copied === 'key' ? '[ OK ]' : '[ COPY ]'}
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest">
                Try it — check a balance
              </p>
              <pre className="bg-terminal-black text-terminal-green font-mono text-xs p-4 rounded border border-terminal-orange-dim overflow-x-auto whitespace-pre-wrap break-all">
                {curlCommand}
              </pre>
              <button
                onClick={() => copyToClipboard('curl')}
                className="terminal-button px-3 py-2 text-xs font-mono"
              >
                {copied === 'curl' ? '[ COPIED ]' : '[ COPY cURL ]'}
              </button>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setStep(1)}
                className="terminal-button px-6 py-3 font-mono text-sm tracking-wide"
              >
                NEXT {'>'}{'>'}
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 animate-in fade-in">
            <h2 className="text-terminal-orange-bright text-xl font-mono tracking-wide">
              {'>'} Create a Child Key
            </h2>

            <p className="text-terminal-orange-dim text-sm font-mono leading-relaxed">
              Child keys let your users spend credits. They can&apos;t add or set credits — only you can.
            </p>

            <div className="border border-terminal-orange-dim rounded overflow-hidden">
              <table className="w-full font-mono text-sm">
                <thead>
                  <tr className="border-b border-terminal-orange-dim">
                    <th className="text-left text-terminal-orange-dim px-4 py-2 text-xs uppercase tracking-widest"></th>
                    <th className="text-center text-terminal-orange px-4 py-2 text-xs uppercase tracking-widest">Service Key</th>
                    <th className="text-center text-terminal-orange px-4 py-2 text-xs uppercase tracking-widest">Child Key</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_ROWS.map((row, i) => (
                    <tr key={row.label} className={i < PERMISSION_ROWS.length - 1 ? 'border-b border-terminal-orange-dim/30' : ''}>
                      <td className="text-terminal-orange-dim px-4 py-2">{row.label}</td>
                      <td className="text-center px-4 py-2">
                        <span className="text-terminal-green">&#10003;</span>
                      </td>
                      <td className="text-center px-4 py-2">
                        {row.child
                          ? <span className="text-terminal-green">&#10003;</span>
                          : <span className="text-red-500">&#10007;</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={onCreateChildKey}
                className="terminal-button px-8 py-4 font-mono text-sm tracking-wide terminal-glow"
              >
                [ CREATE YOUR FIRST CHILD KEY ]
              </button>
            </div>

            <div className="flex items-center justify-between pt-4">
              <button
                onClick={() => setStep(2)}
                className="text-terminal-orange-dim text-xs font-mono hover:text-terminal-orange transition-colors underline underline-offset-4"
              >
                skip
              </button>
              <button
                onClick={() => setStep(2)}
                className="terminal-button px-6 py-3 font-mono text-sm tracking-wide"
              >
                NEXT {'>'}{'>'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 animate-in fade-in text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full border-2 border-terminal-green flex items-center justify-center terminal-glow">
                <span className="text-terminal-green text-3xl">&#10003;</span>
              </div>
            </div>

            <h2 className="text-terminal-orange-bright text-xl font-mono tracking-wide">
              {'>'} Ready to Go
            </h2>

            <p className="text-terminal-orange-dim text-sm font-mono">
              You&apos;re all set. Your dashboard is ready.
            </p>

            <a
              href="#"
              className="inline-block text-terminal-orange text-xs font-mono underline underline-offset-4 hover:text-terminal-orange-bright transition-colors"
            >
              docs / bring-your-own-backend
            </a>

            <div className="pt-4">
              <button
                onClick={onComplete}
                className="terminal-button px-8 py-4 font-mono text-sm tracking-wide terminal-glow"
              >
                [ GO TO DASHBOARD ]
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
